import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSettings, useCustomer } from "hooks/useStore";
import SearchInput from "./input";
import SearchResult from "./result";
import useFocusTrap from "hooks/useFocusTrap";
import fetchSearchResults from "services/api/search";
import { debounce } from "lodash";
import { trackViewSearchResults, trackSearchTerm, trackSearchActiveTab } from "analytics";
import { SEARCH_TABS } from "utils/constants";
import clsx from "clsx/lite";
import useToggle from "hooks/useToggle";
import SearchButton from "./button";
import SearchTabs from "./tabs";
import useClickOutside from "hooks/useClickOutside";
import { useMediaQuery } from "react-responsive";
import { SCREEN_SIZES } from "utils/constants";
import Image from "components/image";
import { ReactComponent as Close } from "svgs/close.svg";

const performSearch = debounce(
	async (query, setResults, setLoading, customer, activeTab) => {
		setLoading(true);
		try {
			const resp = await fetchSearchResults(query, true, 9);
			setResults(resp);
			trackViewSearchResults(resp, query, customer, "quick", activeTab);
		} catch (error) {
			console.error("Error fetching search results:", error);
		} finally {
			setLoading(false);
		}
	},
	300
);

const Search = ({ data, isEmbedded = false, isModule = false, variation2 = false, embedHeader, logo }) => {
	const { isSearchOpen, toggleSearch } = useSettings(["isSearchOpen", "toggleSearch"]);
	const customer = useCustomer(["id", "email", "tags", "defaultAddress", "isAuthenticated"]);

	const [query, setQuery] = useState("");
	const [searchResults, setSearchResults] = useState({ terms: [], products: [], blogs: [] });
	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState(SEARCH_TABS.PRODUCTS);
	const [isInputFocused, toggleInputFocus] = useToggle();
	const { popularTerms = [], popularProducts = [], popularBlogs = [] } = data || {};
	const searchRef = useRef(null);
	const isMobile = useMediaQuery({ maxWidth: SCREEN_SIZES.sm });
	const searchWrapperRef = useClickOutside(() => resetSearchState());

	useFocusTrap(isSearchOpen, searchRef);

	const updateSearchResults = useCallback(() => {
		if (query.trim()) {
			performSearch(query, setSearchResults, setLoading, customer, activeTab);
			const timeout = setTimeout(() => trackSearchTerm(query, activeTab), 2000);
			return () => clearTimeout(timeout);
		}
		setSearchResults({});
	}, [query, customer, activeTab]);

	useEffect(updateSearchResults, [query]);

	const clear = () => {
		setQuery("");
		setSearchResults([]);
	};

	const handleTabChange = (tab) => {
		setActiveTab(tab);
		trackSearchActiveTab(tab);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isInputFocused && searchRef.current && !searchRef.current.contains(event.target)) toggleInputFocus();
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isInputFocused, toggleInputFocus]);

	const resetSearchState = () => {
		if (!isMobile) return;
		if (isInputFocused) toggleInputFocus();
		if (query) setQuery("");
		if (isSearchOpen) toggleSearch();
	};

	const showResults = query || isInputFocused || isSearchOpen;
	const showSearchPanel = isSearchOpen || isEmbedded;


	return (
		<div ref={searchRef} className={clsx("flex", variation2 && "search-variation-2")}>
			{!variation2 && <SearchButton embedHeader={embedHeader} />}
			{variation2 &&
				<SearchInput value={query} setActiveTab={handleTabChange} activeTab={activeTab}
					clear={clear} toggleInputFocus={toggleInputFocus} isInputFocused={isInputFocused}
					onChange={(value) => setQuery(value)} variation2 embedHeader={embedHeader}
				/>
			}
			{showSearchPanel &&
				<div className={clsx(isEmbedded ? "w-full" : "search-container search-container-t59", isModule && "module-search")}>
					<div className={clsx("standard-section-width mx-auto", !isEmbedded && "horizontal-padding")} ref={searchWrapperRef}>
						{!variation2 ?
							<>
								<div className="hidden-t59">
									<SearchInput value={query} setActiveTab={handleTabChange} activeTab={activeTab}
										clear={clear} toggleInputFocus={toggleInputFocus} isInputFocused={isInputFocused}
										onChange={(value) => setQuery(value)} isModule={isModule}
									/></div>
								<div className="hidden gap-10 items-center mb-4 search-bar-t59">
									<Image image={logo} onClick={() => isSearchOpen && toggleSearch()}
										className={clsx("my-auto", variation2 ? "w-[68px]" : "w-24", embedHeader && "invert")} 
										alt="Company Logo" />
									<SearchInput value={query} setActiveTab={handleTabChange} activeTab={activeTab}
										clear={clear} toggleInputFocus={toggleInputFocus} isInputFocused={isInputFocused}
										onChange={(value) => setQuery(value)} isModule={isModule} results={searchResults}
									/>
									<button onClick={() => isSearchOpen && toggleSearch()} 
										className="flex text-black animate-fadeIn">
										<Close className="size-5" />
									</button>
								</div>
							</>
							:
							<div className="w-fit standard-section-width mx-auto">
								<SearchTabs
									className="hidden md:flex"
									activeTab={activeTab}
									setActiveTab={setActiveTab}
								/>
							</div>
						}
						{showResults && (
							<SearchResult query={query} results={searchResults} loading={loading}
								popularData={{ popularProducts, popularBlogs, popularTerms }}
								activeTab={activeTab}
							/>
						)}
					</div>
				</div>
			}
		</div>
	);
};

export default Search;